import React from 'react';
import CreatableSelect from 'react-select/creatable';
import { components } from 'react-select';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';

function arrayMove(array, from, to) {
  const slicedArray = array.slice();
  slicedArray.splice(
    to < 0 ? array.length + to : to,
    0,
    slicedArray.splice(from, 1)[0]
  );
  return slicedArray;
}

const SortableMultiValue = SortableElement(
  (props) => {
    const onMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const innerProps = { ...props.innerProps, onMouseDown };
    return <components.MultiValue {...props} innerProps={innerProps} />;
  }
);

const SortableMultiValueLabel = SortableHandle(
  (props) => <components.MultiValueLabel {...props} />
);

const SortableCreatableSelect = SortableContainer(CreatableSelect);

const createOption = (label) => ({
  label,
  value: label,
});

const CreatableSortableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Type something and press enter...",
  hideDropdownIndicator = true,
  closeMenuOnCreate = true,
  isValidNewOption,
  formatCreateLabel,
  onCreateOption,
  onInputChange: customOnInputChange,
  ...restProps
}) => {
  const [inputValue, setInputValue] = React.useState('');
  const [menuIsOpen, setMenuIsOpen] = React.useState(undefined);

  const handleKeyDown = (event) => {
    if (!inputValue) return;
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        // Check if the option is valid before creating
        if (isValidNewOption && !isValidNewOption(inputValue, value, value || [])) {
          return;
        }

        // Use custom create function if provided, otherwise default
        const newOption = onCreateOption
          ? onCreateOption(inputValue)
          : createOption(inputValue);

        if (newOption) {
          onChange([...(value || []), newOption]);
          setInputValue('');

          // Close menu after creating if closeMenuOnCreate is true
          if (closeMenuOnCreate) {
            setMenuIsOpen(false);
          }

          event.preventDefault();
        }
    }
  };

  const handleInputChange = (newValue, actionMeta) => {
    // If custom input change handler provided, use it
    if (customOnInputChange) {
      const processedValue = customOnInputChange(newValue, actionMeta);
      setInputValue(processedValue);
    } else {
      setInputValue(newValue);
    }

    // Open menu when typing (unless it was explicitly closed)
    if (actionMeta.action === 'input-change' && newValue) {
      setMenuIsOpen(undefined); // Let react-select handle it naturally
    }
  };

  const handleChange = (newValue, actionMeta) => {
    onChange(newValue || []);

    // Close menu after selecting/creating if closeMenuOnCreate is true
    if (closeMenuOnCreate && actionMeta.action === 'create-option') {
      setMenuIsOpen(false);
      setInputValue('');
    }
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const newValue = arrayMove(value || [], oldIndex, newIndex);
    onChange(newValue);
  };


  const customComponents = {
    ...(hideDropdownIndicator ? { DropdownIndicator: null } : {}),
    MultiValue: SortableMultiValue,
    MultiValueLabel: SortableMultiValueLabel,
    ...restProps.components,
  };


  return (
    <SortableCreatableSelect
      useDragHandle
      // react-sortable-hoc props:
      axis="xy"
      onSortEnd={onSortEnd}
      distance={4}
      getHelperDimensions={({ node }) => node.getBoundingClientRect()}
      // react-select props:
      components={customComponents}
      inputValue={inputValue}
      isClearable
      isMulti
      menuIsOpen={menuIsOpen}
      onMenuOpen={() => setMenuIsOpen(true)}
      onMenuClose={() => setMenuIsOpen(false)}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      value={value}
      options={options}
      isValidNewOption={isValidNewOption}
      formatCreateLabel={formatCreateLabel}
      noOptionsMessage={() => null}
      {...restProps}
    />
  );
};

export default CreatableSortableSelect;
